import { computed, Injectable, signal } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private instance!: Keycloak;
  private initialized = false;

  private readonly token = signal<string | null>(null);
  public readonly isAuthenticated = computed(() => !!this.token());

  private create() {
    this.instance = new Keycloak({
      url: environment.AUTH_CONFIG.HOST,
      realm: environment.AUTH_CONFIG.REALM,
      clientId: environment.AUTH_CONFIG.CLIENT_ID,
    });
  }

  public async ensureInit() {
    if (this.initialized) return;

    // Public routes that must not trigger a Keycloak login-required redirect
    if (
      window.location.pathname.startsWith('/register') ||
      window.location.pathname.startsWith('/forgot-password') ||
      window.location.pathname.startsWith('/reset-password')
    )
      return;

    if (!this.instance) this.create();

    const redirectUri = this.getLoginRedirectUri();

    const authenticated = await this.initAdapter({
      onLoad: 'login-required',
      redirectUri,
    });

    if (this.instance.token) {
      this.token.set(this.instance.token);
    }

    // login-required should always authenticate or redirect; guard against a dead-end
    if (!authenticated || !this.token()) {
      await this.instance.login({ redirectUri });
      return;
    }

    this.instance.onTokenExpired = async () => {
      const refreshed = await this.instance.updateToken(30);
      if (refreshed && this.instance.token) this.token.set(this.instance.token);
    };
  }

  public getToken() {
    return this.token();
  }

  public getUserAttributes() {
    return this.instance.loadUserProfile();
  }

  // Invite flow returns to /accept-invite; every other flow returns to /.
  public getLoginRedirectUri(): string {
    const path = window.location.pathname;
    const stored = sessionStorage.getItem('postLoginRedirect');

    if (path.startsWith('/accept-invite') || stored?.startsWith('/accept-invite')) {
      return `${window.location.origin}/accept-invite`;
    }

    return `${window.location.origin}/`;
  }

  public async login(redirectUri = this.getLoginRedirectUri()) {
    // create instance if not already done in ensureInit (e.g. for public routes like /register, /forgot-password, /reset-password)
    if (!this.instance) this.create();

    // Adapter may already be initialized by ensureInit without this.initialized having
    // been set (e.g. init succeeded but login redirect was still required).
    if (!this.initialized) {
      await this.initAdapter({ redirectUri });
    }

    return this.instance.login({ redirectUri });
  }

  public logout(redirectUri = `${window.location.origin}`) {
    return this.instance.logout({ redirectUri });
  }

  public setRedirectPath(path: string) {
    sessionStorage.setItem('postLoginRedirect', path);
  }

  public consumeRedirectPath(): string | null {
    const path = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
    return path;
  }

  public async refreshToken(force = false): Promise<boolean> {
    try {
      const minValidity = force ? -1 : 30;

      const refreshed = await this.instance.updateToken(minValidity);

      if (refreshed) {
        console.debug('Token was successfully refreshed');
        this.token.set(this.instance.token ?? null);
      }

      return refreshed;
    } catch (error) {
      console.error('Failed to refresh token or session expired', error);
      this.token.set(null);
      return false;
    }
  }

  // Init Keycloak once, and mark it initialized so login() cannot init again.
  private async initAdapter(options: {
    onLoad?: 'login-required' | 'check-sso';
    redirectUri: string;
  }): Promise<boolean> {
    if (this.initialized || this.instance.didInitialize) {
      this.initialized = true;
      return !!this.instance.authenticated;
    }

    const authenticated = await this.instance.init({
      onLoad: options.onLoad,
      pkceMethod: 'S256',
      flow: 'standard',
      responseMode: 'query',
      checkLoginIframe: false,
      redirectUri: options.redirectUri,
    });

    this.initialized = true;
    return authenticated;
  }
}
